// Transient champion UI state shared between DOM panels, the tile-click
// handler and the R3F canvas (React context does not cross the canvas
// boundary, a zustand store does): ability targeting mode and the queue of
// Firestorm casts the VFX layer drains.

import { create } from 'zustand';
import type { CastHint } from './castHint.js';

export type Vec3 = readonly [number, number, number];

export interface Targeting {
  readonly unitId: string;
  readonly slot: number;
}

export interface FirestormCast {
  readonly id: number;
  /** Dragon mouth (board space). */
  readonly from: Vec3;
  /** Blast centre (board space). */
  readonly to: Vec3;
}

/** A one-shot cheap VFX for a non-Firestorm ability (registry id + board-space position). */
export interface ChampionEffect {
  readonly id: number;
  readonly defId: string;
  readonly position: Vec3;
}

interface ChampionUiState {
  readonly targeting: Targeting | null;
  readonly lastError: string | null;
  /** Ability key the last rejected cast was for (picks a specific message). */
  readonly lastErrorKey: string | null;
  /** Non-blocking note about the last accepted cast (the not-at-war hint). */
  readonly lastNotice: CastHint | null;
  /** Damage events of the last accepted cast; null when unknown. */
  readonly lastCastHits: number | null;
  readonly firestorms: ReadonlyArray<FirestormCast>;
  /** Withering / Bulwark / Ascend effects the VFX layer drains. */
  readonly effects: ReadonlyArray<ChampionEffect>;
  /**
   * The local champion's ability keys by slot (set by the provider). The sim's
   * `legend:ability_activated` event carries `key` since the protocol fix, so
   * this is only the fallback for older events that carried just the slot.
   */
  readonly localAbilityKeys: ReadonlyArray<string>;
  startTargeting: (t: Targeting) => void;
  cancelTargeting: () => void;
  setError: (code: string | null, abilityKey?: string | null) => void;
  setNotice: (notice: CastHint | null) => void;
  setCastHits: (hits: number | null) => void;
  pushFirestorm: (from: Vec3, to: Vec3) => void;
  drainFirestorms: () => ReadonlyArray<FirestormCast>;
  pushEffect: (defId: string, position: Vec3) => void;
  drainEffects: () => ReadonlyArray<ChampionEffect>;
  setLocalAbilityKeys: (keys: ReadonlyArray<string>) => void;
}

let nextCastId = 0;

export const useChampionUi = create<ChampionUiState>()((set, get) => ({
  targeting: null,
  lastError: null,
  lastErrorKey: null,
  lastNotice: null,
  lastCastHits: null,
  firestorms: [],
  effects: [],
  localAbilityKeys: [],
  startTargeting: (targeting) => set({ targeting, lastError: null, lastNotice: null }),
  cancelTargeting: () => set({ targeting: null }),
  setError: (lastError, abilityKey) => set({ lastError, lastErrorKey: abilityKey ?? null }),
  setNotice: (lastNotice) => set({ lastNotice }),
  setCastHits: (lastCastHits) => set({ lastCastHits }),
  pushFirestorm: (from, to) =>
    set((s) => ({ firestorms: [...s.firestorms, { id: nextCastId++, from, to }] })),
  drainFirestorms: () => {
    const queued = get().firestorms;
    if (queued.length > 0) set({ firestorms: [] });
    return queued;
  },
  pushEffect: (defId, position) =>
    set((s) => ({ effects: [...s.effects, { id: nextCastId++, defId, position }] })),
  drainEffects: () => {
    const queued = get().effects;
    if (queued.length > 0) set({ effects: [] });
    return queued;
  },
  setLocalAbilityKeys: (keys) => {
    const cur = get().localAbilityKeys;
    if (cur.length !== keys.length || cur.some((k, i) => k !== keys[i])) {
      set({ localAbilityKeys: keys });
    }
  },
}));

/** A refused move: a fortified Golem cannot move, so say why in its Champion panel. */
export function reportMoveRejection(response: unknown): void {
  const code = (response as { error?: { message?: string } }).error?.message;
  if (code === 'fortified') useChampionUi.getState().setError(code);
}
